import { create } from "zustand";
import { toast } from "react-toastify";
import {
  addSlaExceptionRule,
  getAllActionCategories,
  getSlaExceptionRuleList,
  updateSlaExceptionRule,
} from "../../../../Apis/complaintApi";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  paginateClientList,
  sortActiveFirst,
  buildClientPagination,
} from "../../utils/complaintListUtils";

export const getActionCategoryLabel = (value, actionCategories = []) => {
  if (value == null || value === "") return "N/A";

  const stringValue = String(value).trim();
  const matchedCategory = actionCategories.find(
    (category) =>
      String(category.actionCategoryId) === stringValue ||
      category.actionCategoryName?.trim() === stringValue
  );

  return matchedCategory?.actionCategoryName || stringValue;
};

const mapSlaExceptionRuleItem = (item, actionCategories = []) => ({
  slaExceptionRuleId: item.slaExceptionId ?? item.slaExceptionRuleId ?? item.id,
  slaExceptionId: item.slaExceptionId ?? item.slaExceptionRuleId ?? item.id,
  zoneId: item.zoneId,
  zoneName: item.zoneName ?? "",
  circleId: item.circleId ?? item.wardId,
  wardNo: item.wardName ?? item.wardNo ?? item.circleName ?? "",
  wardName: item.wardName ?? item.wardNo ?? "",
  actionCategory:
    item.actionCategory != null ? String(item.actionCategory) : "",
  actionCategoryName:
    getActionCategoryLabel(item.actionCategory, actionCategories) ||
    item.actionCategory ||
    "",
  remarks: item.remarks ?? "",
  validFrom: item.validFrom ?? "",
  validTill: item.validTo ?? item.validTill ?? "",
  validTo: item.validTo ?? item.validTill ?? "",
  isActive: Boolean(item.isActive),
  status: item.status,
  lastModifiedOn: item.lastModifiedOn ?? item.modifiedDate ?? "",
});

const mapSlaExceptionRuleList = (response, actionCategories = []) => {
  if (!response) return [];

  const list =
    response?.data ??
    response?.result ??
    response?.items ??
    (Array.isArray(response) ? response : []);

  return Array.isArray(list)
    ? list.map((item) => mapSlaExceptionRuleItem(item, actionCategories))
    : [];
};

const getErrorMessage = (
  error,
  fallback = "Unable to save SLA exception rule details."
) => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData) return responseData;
  return responseData?.message || responseData?.Message || fallback;
};

export const useSlaExceptionRuleStore = create((set, get) => ({
  slaExceptionRules: [],
  filteredSlaExceptionRules: [],
  actionCategories: [],
  isLoading: false,
  isSaving: false,
  filters: {
    searchTerm: "",
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  fetchActionCategories: async () => {
    try {
      const categories = await getAllActionCategories();
      set({ actionCategories: categories });
      return categories;
    } catch (error) {
      console.error("Error fetching action categories:", error);
      set({ actionCategories: [] });
      return [];
    }
  },

  resetFilters: async () => {
    set({ filters: { searchTerm: "" } });
    return get().fetchSlaExceptionRules({ currentPage: 1, searchTerm: "" });
  },

  fetchSlaExceptionRules: async (options = {}) => {
    const { filters, pagination, actionCategories } = get();
    const currentPage = options.currentPage ?? pagination.currentPage;
    const pageSize = options.pageSize ?? pagination.pageSize;
    const search = (options.searchTerm ?? filters.searchTerm)?.trim() ?? "";
    const categories =
      actionCategories.length > 0
        ? actionCategories
        : await get().fetchActionCategories();

    set({
      isLoading: true,
      filters: { searchTerm: search },
    });

    try {
      const response = await getSlaExceptionRuleList({
        pageIndex: 1,
        pageSize: COMPLAINT_MAX_FETCH_SIZE,
        search,
      });

      const sorted = sortActiveFirst(
        mapSlaExceptionRuleList(response, categories)
      );
      const pagedRecords = paginateClientList(sorted, currentPage, pageSize);
      const nextPagination = buildClientPagination(
        sorted.length,
        currentPage,
        pageSize
      );

      set({
        slaExceptionRules: sorted,
        filteredSlaExceptionRules: pagedRecords,
        pagination: nextPagination,
        isLoading: false,
      });

      return { success: true, data: pagedRecords, pagination: nextPagination };
    } catch (error) {
      console.error("Error fetching SLA exception rules:", error);

      set({
        slaExceptionRules: [],
        filteredSlaExceptionRules: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });

      return { success: false, error };
    }
  },

  fetchAllSlaExceptionRulesForExport: async () => {
    const { filters, actionCategories } = get();
    const categories =
      actionCategories.length > 0
        ? actionCategories
        : await get().fetchActionCategories();
    const search = filters.searchTerm?.trim() ?? "";

    try {
      const response = await getSlaExceptionRuleList({
        pageIndex: 1,
        pageSize: COMPLAINT_MAX_FETCH_SIZE,
        search,
      });

      return sortActiveFirst(mapSlaExceptionRuleList(response, categories));
    } catch (error) {
      console.error("Error fetching SLA exception rules for export:", error);
      return get().filteredSlaExceptionRules;
    }
  },

  saveSlaExceptionRule: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      const apiPayload = {
        zoneId: payload.zoneId,
        wardId: payload.wardId ?? payload.circleId,
        actionCategoryId: payload.actionCategoryId ?? payload.actionCategory,
        remarks: payload.remarks,
        validFrom: payload.validFrom,
        validTo: payload.validTill,
        isActive: payload.isActive,
      };

      if (isEdit) {
        apiPayload.slaExceptionId =
          payload.slaExceptionId ?? payload.slaExceptionRuleId;
      }

      const response = isEdit
        ? await updateSlaExceptionRule(apiPayload)
        : await addSlaExceptionRule(apiPayload);

      const isSuccess = response?.success === true;

      if (!isSuccess) {
        const message =
          response?.message || "Unable to save SLA exception rule details.";
        toast.error(message);
        return { success: false, message };
      }

      toast.success(
        response?.message ||
          (isEdit
            ? "SLA Exception Rule updated successfully."
            : "SLA Exception Rule added successfully.")
      );

      await get().fetchSlaExceptionRules({
        currentPage: isEdit ? get().pagination.currentPage : 1,
      });

      return { success: true, message: response?.message };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return { success: false, message, error };
    } finally {
      set({ isSaving: false });
    }
  },
}));
